import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const annotationSteps = [
  { label: '专家评语', content: '"问题意识较强"', icon: 'ri-chat-quote-line', color: 'accent' as const },
  { label: '课程编号', content: '2024-SZ-0147', icon: 'ri-barcode-line', color: 'primary' as const },
  { label: '证据片段', content: '22:10 学生回应 + 教师追问', icon: 'ri-video-line', color: 'accent' as const },
  { label: '指标标签', content: '互动设计 · 问题意识', icon: 'ri-price-tag-3-line', color: 'primary' as const },
  { label: '训练样本', content: '专家判断 ↔ 课堂证据 对应关系', icon: 'ri-database-2-line', color: 'accent' as const },
];

export default function P14ExpertToSample({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            从经验到数据
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            一条专家意见<span className="text-primary-600">如何变成AI可学习样本</span>
          </h2>
          <p className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI学习的不是"漂亮话"，而是专家判断和课堂证据之间的对应关系
          </p>
        </div>

        {/* Expert comment card - starting point */}
        <div className={`max-w-xl mx-auto mb-8 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="rounded-2xl glass-strong border-accent-200/30 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl glass-gold border-accent-200/40 flex items-center justify-center">
                <i className="ri-chat-quote-line text-lg text-accent-600" />
              </div>
              <div>
                <p className="text-xs text-foreground-400 font-body">专家原始评语</p>
                <p className="font-heading text-base font-semibold text-foreground-800">问题意识较强</p>
              </div>
              <div className="ml-auto text-xs text-foreground-300 font-mono">占位符</div>
            </div>
            <div className="flex items-center justify-center">
              <i className="ri-arrow-down-line text-2xl text-primary-400 animate-bounce" />
            </div>
          </div>
        </div>

        {/* Annotation decomposition */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {annotationSteps.map((step, i) => (
            <div
              key={step.label}
              className={`group relative p-4 md:p-5 rounded-2xl transition-all duration-500 hover:scale-[1.03] ${step.color === 'accent' ? 'glass-gold border-accent-200/30' : 'glass-liquid border-primary-200/30'}`}
              style={{ transitionDelay: `${500 + i * 120}ms` }}
            >
              <div className="relative z-10">
                <div className={`w-9 h-9 rounded-xl mb-3 flex items-center justify-center ${step.color === 'accent' ? 'glass-gold border-accent-200/40' : 'glass-blue border-primary-200/40'}`}>
                  <i className={`${step.icon} text-base ${step.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`} />
                </div>
                <p className="text-xs text-foreground-400 font-body mb-1">{step.label}</p>
                <p className="text-sm font-body font-medium text-foreground-800">{step.content}</p>
              </div>
              {i < annotationSteps.length - 1 && (
                <div className="hidden lg:block absolute -right-2.5 top-1/2 -translate-y-1/2 z-20">
                  <i className="ri-arrow-right-s-line text-lg text-foreground-300" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`mt-8 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass border-white/50">
            <i className="ri-sparkling-line text-accent-500" />
            <span className="text-sm font-body text-foreground-600">
              它需要被拆成：<strong className="text-primary-700">哪个问题？什么时间点？学生有没有回应？教师有没有追问？</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}